import React, { useState, useEffect, useCallback } from 'react';
import { X, RefreshCw, Navigation2, Zap, ZapOff, Gauge, Thermometer, MapPin, ExternalLink, Loader2, AlertCircle, Milestone } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function headingToCompass(degrees) {
  if (degrees == null) return '—';
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const idx = Math.round(((degrees % 360) + 360) % 360 / 45) % 8;
  return dirs[idx];
}

function formatTimestamp(ts) {
  if (!ts) return '—';
  const d = new Date(ts * 1000);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function speedColor(speed) {
  const s = parseFloat(speed) || 0;
  if (s === 0) return 'text-gray-400';
  if (s < 40) return 'text-green-400';
  if (s < 80) return 'text-yellow-400';
  return 'text-red-400';
}

/**
 * VehicleTrackModal — works in two modes:
 *   - Trip mode:    pass `trip`    → calls /api/trips/{id}/track
 *   - Vehicle mode: pass `vehicle` → calls /api/vehicles/{id}/track
 */
const VehicleTrackModal = ({ trip, vehicle, onClose }) => {
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [refreshing, setRefreshing]   = useState(false);

  // Derive display info and API path from whichever prop is provided
  const isVehicleMode = !trip && !!vehicle;
  const apiPath = trip
    ? `${API}/api/trips/${trip.id}/track`
    : `${API}/api/vehicles/${vehicle.id}/track`;

  const headerTitle    = trip?.trip_number || vehicle?.registration_number || 'Vehicle';
  const headerSubtitle = trip?.vehicle_registration
    || (vehicle ? `${vehicle.vehicle_type_name || ''}${vehicle.vehicle_size ? ' | ' + vehicle.vehicle_size : ''}` : '');

  const fetchLocation = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await fetch(apiPath, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to fetch location');
      }
      const json = await res.json();
      setData(json);
      setLastRefreshed(new Date());
    } catch (e) {
      setError(e.message || 'Unable to reach tracking service');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [apiPath]);

  useEffect(() => {
    fetchLocation();
    const timer = setInterval(() => fetchLocation(), 30000);
    return () => clearInterval(timer);
  }, [fetchLocation]);

  const mapsUrl = data?.available && data.latitude && data.longitude
    ? `https://www.google.com/maps?q=${data.latitude},${data.longitude}`
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.72)' }}>
      <div className="bg-gray-900 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden border border-gray-700">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 bg-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Navigation2 className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-white font-semibold font-ibm text-sm">{headerTitle}</p>
              {headerSubtitle && (
                <p className="text-gray-400 font-ibm text-xs">{headerSubtitle}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="track-refresh-btn"
              onClick={() => fetchLocation(true)}
              disabled={refreshing || loading}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <button
              data-testid="track-close-btn"
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5">
          {loading && (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
              <p className="text-gray-400 font-ibm text-sm">Fetching location…</p>
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
              <AlertCircle className="w-8 h-8 text-red-400" />
              <p className="text-gray-300 font-ibm text-sm">{error}</p>
              <button
                onClick={() => fetchLocation(true)}
                className="text-xs text-blue-400 hover:text-blue-300 font-ibm border border-blue-400/30 px-3 py-1.5 rounded-lg mt-1"
              >
                Try again
              </button>
            </div>
          )}

          {!loading && !error && data && !data.available && (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
              <AlertCircle className="w-8 h-8 text-yellow-400" />
              <p className="text-gray-300 font-ibm text-sm">
                {data.message || 'Vehicle not found in FMS'}
              </p>
              <p className="text-gray-500 font-mono text-xs mt-1">FMS lookup: {data.fms_name}</p>
            </div>
          )}

          {!loading && !error && data?.available && (
            <div className="space-y-3">
              {/* Ignition + Speed */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-800 rounded-xl p-3.5 flex items-center gap-3">
                  {data.ignition?.toLowerCase() === 'on' ? (
                    <Zap className="w-5 h-5 text-green-400 flex-shrink-0" />
                  ) : (
                    <ZapOff className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  )}
                  <div>
                    <p className="text-gray-500 font-ibm text-xs uppercase tracking-wide">Ignition</p>
                    <p className={`font-semibold font-ibm text-sm ${data.ignition?.toLowerCase() === 'on' ? 'text-green-400' : 'text-gray-400'}`}>
                      {data.ignition || 'Unknown'}
                    </p>
                  </div>
                </div>
                <div className="bg-gray-800 rounded-xl p-3.5 flex items-center gap-3">
                  <Gauge className={`w-5 h-5 flex-shrink-0 ${speedColor(data.speed)}`} />
                  <div>
                    <p className="text-gray-500 font-ibm text-xs uppercase tracking-wide">Speed</p>
                    <p className={`font-semibold font-ibm text-sm ${speedColor(data.speed)}`}>
                      {parseFloat(data.speed || 0).toFixed(0)} km/h
                    </p>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="bg-gray-800 rounded-xl p-3.5">
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-gray-500 font-ibm text-xs uppercase tracking-wide mb-1">Current Location</p>
                    <p className="text-gray-200 font-ibm text-sm leading-relaxed">{data.address || 'Address unavailable'}</p>
                  </div>
                </div>
              </div>

              {/* Lat/Long + Heading */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-800 rounded-xl p-3.5">
                  <p className="text-gray-500 font-ibm text-xs uppercase tracking-wide mb-1">Coordinates</p>
                  <p className="text-gray-300 font-mono text-xs leading-relaxed">
                    {data.latitude?.toFixed(6)}<br />{data.longitude?.toFixed(6)}
                  </p>
                </div>
                <div className="bg-gray-800 rounded-xl p-3.5">
                  <p className="text-gray-500 font-ibm text-xs uppercase tracking-wide mb-1">Heading</p>
                  <p className="text-gray-300 font-ibm text-sm font-semibold">
                    {headingToCompass(data.heading)} <span className="text-gray-500 text-xs font-normal">({data.heading}°)</span>
                  </p>
                  {data.temperature != null && data.temperature !== 0 && (
                    <div className="flex items-center gap-1 mt-2">
                      <Thermometer className="w-3.5 h-3.5 text-blue-300" />
                      <span className="text-blue-300 font-ibm text-xs">{data.temperature}°C</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Odometer */}
              {data.odometer && (
                <div className="bg-gray-800 rounded-xl p-3.5 flex items-center gap-3">
                  <Milestone className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <div>
                    <p className="text-gray-500 font-ibm text-xs uppercase tracking-wide">Odometer</p>
                    <p className="text-gray-300 font-ibm text-sm font-semibold">
                      {parseFloat(data.odometer).toLocaleString('en-IN')} km
                    </p>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <p className="text-gray-600 font-ibm text-xs text-center">
                FMS updated: {formatTimestamp(data.timestamp)}
                {lastRefreshed && (
                  <span className="ml-2">· Fetched {lastRefreshed.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                )}
              </p>
            </div>
          )}
        </div>

        {/* Footer — Google Maps */}
        {!loading && data?.available && mapsUrl && (
          <div className="px-5 pb-5">
            <a
              data-testid="track-open-maps-link"
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-ibm font-medium text-sm transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Open in Google Maps
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleTrackModal;
